import { DEFAULT_MULTIPLIER, PAGE_BUILDER_BADGE_SELECTOR } from './constants';
import { normalizeMultiplier, parseNumber, parsePriceText } from './helpers';
import { fetchRouteMultipliersBatched } from './route-multiplier-fetch';
import { revealBadge, setBadgeContent } from './render';

function getPageBuilderBadgeData(price, multiplier) {
    if (Number.isNaN(price) || price <= 0) {
        return null;
    }

    const multiplierValue = normalizeMultiplier(multiplier);

    return {
        totalEntries: price * multiplierValue,
        multiplier: multiplierValue,
    };
}

function getPageBuilderBadges(root = document) {
    return Array.from(root.querySelectorAll(PAGE_BUILDER_BADGE_SELECTOR));
}

function getPageBuilderBadgePrice(badge) {
    const priceContainer = badge.previousElementSibling;

    if (!priceContainer) {
        return NaN;
    }

    return parsePriceText(priceContainer.textContent);
}

const MAX_HYDRATION_PASSES = 3;

export function createPageBuilderHydrator({ multiplierCache, saveCache }) {
    let activeMultiplierRequest = null;
    let consecutivePasses = 0;

    function applyPageBuilderBadgeText(root = document) {
        getPageBuilderBadges(root).forEach((badge) => {
            const price = getPageBuilderBadgePrice(badge);
            const productPath = badge.dataset.productPath || '';
            const multiplier = multiplierCache.has(productPath) ? multiplierCache.get(productPath) : NaN;

            if (productPath && !multiplierCache.has(productPath)) {
                return;
            }

            const badgeData = getPageBuilderBadgeData(price, multiplier);

            if (!badgeData) {
                return;
            }

            setBadgeContent(badge, badgeData);
            revealBadge(badge);
        });
    }

    return function hydratePageBuilderBadges(storefrontApiToken) {
        const badges = getPageBuilderBadges();
        const pathsToFetch = Array.from(new Set(
            badges
                .map((badge) => badge.dataset.productPath || '')
                .filter((path) => path && !multiplierCache.has(path)),
        ));

        if (pathsToFetch.length === 0) {
            consecutivePasses = 0;
            applyPageBuilderBadgeText();
            return;
        }

        if (!storefrontApiToken) {
            pathsToFetch.forEach((path) => {
                multiplierCache.set(path, DEFAULT_MULTIPLIER);
            });
            saveCache();
            applyPageBuilderBadgeText();
            return;
        }

        if (activeMultiplierRequest) {
            return;
        }

        activeMultiplierRequest = fetchRouteMultipliersBatched(pathsToFetch, storefrontApiToken)
            .then((multipliersByPath) => {
                pathsToFetch.forEach((path) => {
                    multiplierCache.set(path, multipliersByPath[path] || DEFAULT_MULTIPLIER);
                });
                applyPageBuilderBadgeText();
            })
            .catch((error) => {
                console.warn('[Raffle] Page Builder multiplier fetch failed:', error);
                pathsToFetch.forEach((path) => {
                    multiplierCache.set(path, DEFAULT_MULTIPLIER);
                });
                applyPageBuilderBadgeText();
            })
            .finally(() => {
                saveCache();
                activeMultiplierRequest = null;
                consecutivePasses += 1;

                if (consecutivePasses < MAX_HYDRATION_PASSES) {
                    hydratePageBuilderBadges(storefrontApiToken);
                } else {
                    consecutivePasses = 0;
                }
            });
    };
}
